import { Stack } from '@mantine/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { AddButtonLink, Browse, usePagination } from '@/core/components';

import { FilterBox, Id, SearchFilter, useSearch, useSortDirection } from '@feature/utils';

import { CitationCard } from './components/Card/CitationCard';
import { getCitationPagedOptions } from './model/CitationQuery';
import { Citation } from './model/CitationTypes';

export function CitationBrowse({ publicationId }: { publicationId: Id }) {
  const { t } = useTranslation();
  const { sortDirection, toggleSortDirection } = useSortDirection('DESC');
  const { search, searchProps } = useSearch();
  const { pagination, useTotalElements, ...paginationProps } = usePagination({ search });
  const { data } = useSuspenseQuery(
    getCitationPagedOptions(publicationId, {
      ...pagination,
      sortDirection,
      search,
      publicationId: Number(publicationId),
    }),
  );
  useTotalElements(data);

  return (
    <Browse
      actions={<AddButtonLink to="create" />}
      title={t('citations.title')}
      pagination={paginationProps}
      filters={
        <FilterBox>
          <Stack>
            <SearchFilter
              label={t('label.search')}
              description={t('citations.searchBy')}
              {...searchProps}
            />
          </Stack>
        </FilterBox>
      }
      sortDirection={sortDirection}
      toggleSortDirection={toggleSortDirection}
      sortedBy={t('citations.sortedByTitle')}
      data={data.list}
      renderItem={(citation: Citation) => (
        <CitationCard
          key={citation.id}
          citation={citation}
          to={`${citation.id}`}
        />
      )}
    />
  );
}
